import React, { useEffect } from "react";
import "../styles/credit_guard.css";
import { useDispatch, useSelector } from "react-redux";
import appConst from "../Utilities/appConst.json";
import { payment_link } from "../store/modeSlice";
import { successActions } from "../store/successSlice";
import { useHistory, useLocation } from "react-router-dom";
import { loaderActions } from "../store/loaderSlice";
import { footerActions } from "../store/footerSlice";
import { getThanksPageStatus } from "../store/userSlice";
import { creditGuardActions } from "../store/creditGuardSlice";
import useBrazeScreenView from "../hooks/useBrazeScreenView";
import { getUiItemFilterPrefix } from "../Utilities/itemFilter";

const getListenerTarget = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("online_iframe") === "true" ? window : window.parent;
  } catch (e) {
    return window.parent;
  }
};

const normalizeIframeLanguageParam = (url) => {
  if (!url) {
    return url;
  }

  return url.replace(/([?&])language=/g, "$1lang=");
};

const CreditGuard = () => {
  useBrazeScreenView();
  const listenerTarget = React.useMemo(() => getListenerTarget(), []);
  const creditGuardURL = useSelector((state) => state.basket.creditGuardURL);
  const thankYouPageStatus = useSelector(
    (state) => state.user.thankYouPageStatus
  );
  const basket = useSelector((state) => state.basket.basket);
  const footer = useSelector((state) => state.footer.summary);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const iframeSrc = React.useMemo(
    () => normalizeIframeLanguageParam(creditGuardURL),
    [creditGuardURL]
  );

  useEffect(() => {
    dispatch(footerActions.setIsShow(false));
    dispatch(creditGuardActions.setShowIframe(true));

    if (sessionStorage.getItem(appConst.ORDERTOKEN)) {
      dispatch(getThanksPageStatus());
      dispatch(payment_link(sessionStorage.getItem(appConst.ORDERTOKEN)));
    }

    // clean up
    return () => {
      listenerTarget.removeEventListener("message", handler);
      dispatch(creditGuardActions.setShowIframe(false));
    };
  }, []);

  useEffect(() => {
    if (thankYouPageStatus) {
      listenerTarget.addEventListener("message", handler);
    }
    return () => {
      listenerTarget.removeEventListener("message", handler);
    };
  }, [thankYouPageStatus]);

  const handler = async (event) => {
    try {
      const data = await event.data;
      if (data) {
        const id = data.thankyoupageid;
        if (id) {
          switch (id) {
            case appConst.CREDIT_GARD_CANCEL_ID:
              history.push({
                pathname: appConst.FIRST_PAGE,
                search: location.search,
              });
              break;
            case appConst.CREDIT_GARD_MANY_TIME_FAILED_ID:
              dispatch(
                creditGuardActions.setError({
                  isError: true,
                  errorCode: appConst.CREDIT_GARD_MANY_TIME_FAILED_ID,
                })
              );
              history.push({
                pathname: appConst.EIGHTH_PAGE,
                search: location.search,
              });
              break;
            default:
              const thankYouPageData = thankYouPageStatus.find(
                (e) => e.id == id
              );
              if (thankYouPageData) {
                const type = thankYouPageData.type;
                switch (type) {
                  case "success":
                  case "failure":
                    lupaNativePurchase();
                    dispatch(
                      successActions.setSuccess({
                        title: thankYouPageData.title,
                        message: thankYouPageData.message,
                        isSuccess: thankYouPageData.is_success,
                        orderID: basket.order_id,
                      })
                    );
                    history.push({
                      pathname: appConst.ELEVNTH_PAGE,
                      search: location.search,
                    });
                    break;
                  default: {
                    console.log("WebWorker - Unknown action");
                  }
                }
              }
          }
        }
      }
    } catch (e) {}
  };

  useEffect(() => {
    dispatch(loaderActions.loaderShow());
  }, [dispatch]);

  useEffect(() => {
    if (creditGuardURL) {
      dispatch(loaderActions.loaderHide());
    }
  }, [creditGuardURL]);

  const lupaNativePurchase = () => {
    if (typeof window.LupaNative !== "undefined") {
      const orderDetails = creatOrderDetails();
      if (orderDetails) {
        try {
          window.LupaNative.purchase(orderDetails);
        } catch (e) {
          window.LupaNative.exit();
        }
      }
    }
  };

  const creatOrderDetails = () => {
    const orderDetails = {
      totalOrderPrice: Number(footer?.total) || 0, // double
      totalOrderPriceNew: Number(footer?.total) || 0, // double
      usablePoints: basket?.payments?.total_payment || 0, // int
      totalItemPrice: Number(footer?.total) || 0, // double
      totalItemPriceNew: Number(footer?.total) || 0, // double
      totalSaleValue: Number(footer?.total) || 0, // double
      totalCouponValue: Number(footer?.tempAdditionalDiscount) || 0, // double
      shipmentPrice: basket?.delivery?.item_final_price || 0, // double
      shipmentPriceNew: basket?.delivery?.item_final_price || 0, // double
      totalGiftPackagePrice: 0, // double
      autoCoupons: 0, // double
      totalOrderQuantity: Number(basket?.items.length) || 0, // int
      orderID: Number(basket?.order_id), // int
      saleForNL: 0,
      currency: "ILS", // string
      totalCount: basket?.items.length || 0, // int
      countOfCalendars: getCountOfItems(
        basket?.items,
        appConst.VERSION_CALENDAR_PREFIX
      ), // int
      countOfBooks:
        getCountOfItems(basket?.items, appConst.VERSION_TREE_V1_PREFIX) +
        getCountOfItems(basket?.items, appConst.VERSION_TREE_V3_PREFIX), // int
      countOfMinilupa: 0, // int
      countOfTiles: 0, // int
      priceCalendars: 0, // double
      priceBooks: 0, // double
      priceMinilupa: 0, // double
      priceTiles: 0, // double
      priceValueCalendars: 0, // double
      priceValueBooks: 0, // double
      priceValueMinilupa: 0, // double
      priceValueTiles: 0, // double
      shipmentType: basket?.delivery?.title || "", // string
      shipmentDesc: "", // string
      arrItems: basket.items.map((item) => ({
        item_id: item.item_id, // int
        quantity: item.quantity, // int
        event_token: item.url || "", // string
        item_price: item.item_q1_price, // duble
        item_price_with_discount: item.item_final_price, // double
        discount: item.discounts.sale, // double
        formatId: item.format, // int
        formatName: "", // string
        themeName: item.theme?.toString() || "", // string
        layoutName: "", // string
        coverName: item.cover?.toString() || "", // string
        coverId: item.cover_id || 0, // int - could be 0 or 1
        countPages: item.pages || 0, // int
        type: "book", // string
        item_title: item.title || "", // string
        countImages: 0, // int
        arrCoupons: [
          {
            couponOwner: "", // string
            OwnerCode: "", // string
            couponCode: "", // string
            couponValue: 0,
            discountType: "%",
            couponActualValue: 0,
            couponType: "",
          },
        ],
        material: item.material ? item.material.toString() : "", // Convert material to string
        frameColor: item.frame_color ? item.frame_color.toString() : "",
      })),
      delivery_city: "", // string
    };

    return JSON.stringify(orderDetails);
  };

  const getCountOfItems = (items, itemFilterPrefix) => {
    return items.filter((item) => {
      return getUiItemFilterPrefix(item?.item_filter) === itemFilterPrefix;
    }).length;
  };

  return (
    <div className="iframe_container">
      <iframe src={iframeSrc} />
    </div>
  );
};
export default CreditGuard;
